import React, {createContext, useState, useContext, ReactNode, useCallback, useEffect} from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { Building, BuildingModel, BuildingSummary } from "../types/protocols";
import {useBuildingKey} from "./utils";
import { applyComponentPatch, ComponentFieldPatch } from "./componentPatch";

interface BuildingContextType {
    currentBuilding: Building | null;
    loadingBuilding: boolean;
    buildingError: string | null;
    fetchBuilding: (buildingKey: string) => Promise<void>;
    updateComponentFields: (componentKey: string, patch: ComponentFieldPatch) => void;
    clearBuilding: () => void;
}

const BuildingContext = createContext<BuildingContextType>({
    currentBuilding: null,
    loadingBuilding: false,
    buildingError: null,
    fetchBuilding: async () => {},
    updateComponentFields: () => {},
    clearBuilding: () => {},
});

interface BuildingProviderProps {
    children: ReactNode;
}

export const BuildingProvider: React.FC<BuildingProviderProps> = ({ children }) => {
    const [currentBuilding, setCurrentBuilding] = useState<Building | null>(null);
    const [loadingBuilding, setLoadingBuilding] = useState<boolean>(false);
    const [buildingError, setBuildingError] = useState<string | null>(null);

    const fetchBuilding = useCallback(async (buildingKey: string) => {
        if (!buildingKey) {
            setBuildingError('Building key is required');
            return;
        }

        setLoadingBuilding(true);
        setBuildingError(null);

        try {
            const db = getDatabase();
            const modelRef = ref(db, `buildings/${buildingKey}`);
            const modelSnapshot = await get(modelRef);

            const summaryRef = ref(db, `buildingsSummary/${buildingKey}`);
            const summarySnapshot = await get(summaryRef);

            if (modelSnapshot.exists() && summarySnapshot.exists()) {
                const model = modelSnapshot.val() as BuildingModel;
                const summary = summarySnapshot.val() as BuildingSummary;

                const building: Building = {
                    key: buildingKey,
                    model,
                    summary
                };

                setCurrentBuilding(building);
            } else {
                setBuildingError('Byggnaden hittades ej');
            }
        } catch (err) {
            console.error('Error fetching building:', err);
            setBuildingError('Failed to load building data');
        } finally {
            setLoadingBuilding(false);
        }
    }, []);

    /**
     * Apply an edit to a single component in local state so the change is
     * reflected immediately, without re-reading the whole building from Firebase.
     */
    const updateComponentFields = useCallback((componentKey: string, patch: ComponentFieldPatch) => {
        setCurrentBuilding(prev => applyComponentPatch(prev, componentKey, patch));
    }, []);

    const clearBuilding = useCallback(() => {
        setCurrentBuilding(null);
        setBuildingError(null);
    }, []);

    const value = {
        currentBuilding,
        loadingBuilding,
        buildingError,
        fetchBuilding,
        updateComponentFields,
        clearBuilding
    };

    return (
        <BuildingContext.Provider value={value}>
            {children}
        </BuildingContext.Provider>
    );
};

export const useBuilding = () => {
    const context = useContext(BuildingContext);
    const buildingKey = useBuildingKey()
    if (!context) {
        throw new Error('useBuilding must be used within a BuildingProvider');
    }

    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = context;

    useEffect(() => {
        if (buildingKey &&
            (!currentBuilding || currentBuilding.key !== buildingKey) &&
            !loadingBuilding &&
            !buildingError) {
            fetchBuilding(buildingKey).then();
        }
    }, [buildingKey, currentBuilding, loadingBuilding, buildingError, fetchBuilding]);

    return context;
};